/* eslint-disable jsx-a11y/anchor-is-valid */

import dayjs from 'dayjs';
import React, { FC } from 'react';
import NumberFormat from 'react-number-format';
import { Setting, statusMessages } from '../App';
import { useSubscriptionDetail } from '../hooks/use-subscription-detail';
import DatePicker, { registerLocale } from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import weekday from 'dayjs/plugin/weekday';
import 'react-datepicker/dist/react-datepicker.css';
import { Link } from 'react-router-dom';

registerLocale('ja', ja);

export type StatusMessage = keyof typeof statusMessages;

dayjs.extend(weekday);

export const SubscriptionDetail: FC<{ setting: Setting }> = ({ setting }) => {
  const {
    subscription,
    skip,
    updateStatus,
    deliveryDate,
    nextDeliveryDateChange,
    isDisableDays,
    updateDeliveryDays,
    updateStatusOpen,
  } = useSubscriptionDetail(setting);

  if (!subscription) {
    return <></>;
  }

  const {
    subscriptionContractId,
    billingPolicyInterval,
    billingPolicyIntervalCount,
    billingPolicyMinCycles,
    nextBillingDate,
    // customerId,
    status,
    deliveryPriceAmount,
    deliveryPriceCurrencyCode,
    deliveryFormattedAddress,
  } = subscription;

  const billingPolicyIntervals = {
    DAY: '日毎',
    WEEK: '週間毎',
    MONTH: 'ヶ月毎',
    YEAR: '年毎',
  };

  const billingAttempts = [
    ...subscription.billingAttempts.map((billingAttempt) => {
      let status;
      if (!billingAttempt.ready) {
        status = '実行中';
      } else if (billingAttempt.orderId) {
        status = '成功';
      } else {
        status = '失敗';
      }

      return {
        orderId: billingAttempt.orderId,
        orderToken: billingAttempt.orderToken,
        orderName: billingAttempt.orderName,
        status,
        createdAt: billingAttempt.createdAt,
      };
    }),

    {
      orderId: subscription.originOrderId,
      orderToken: subscription.originOrderToken,
      orderName: subscription.originOrderName,
      status: '成功',
      createdAt: subscription.createdAt,
    },
  ];

  // TODO
  const deliveryPriceCurrency = deliveryPriceCurrencyCode === 'JPY' ? '円' : deliveryPriceCurrencyCode;

  let skipButton = <></>;
  let pauseOrActiveButton = <></>;
  let cancelButton = <></>;
  if (subscription.status !== 'CANCELLED') {
    const [action, name] = subscription.status === 'ACTIVE' ? ['paused', '一時停止'] : ['active', '再開'];

    pauseOrActiveButton = subscription.shop.showPause ? (
      <button
        className="huckleberry-subscription-detail-order-btn stop"
        disabled={subscription.disabled}
        onClick={() => {
          updateStatusOpen(action);
        }}
      >
        {name}
      </button>
    ) : (
      <></>
    );

    skipButton = subscription.shop.showSkip ? (
      <button
        className="huckleberry-subscription-detail-order-btn skip"
        disabled={subscription.disabled}
        onClick={() => {
          updateStatusOpen('skip');
        }}
      >
        スキップ
      </button>
    ) : (
      <></>
    );

    cancelButton =
      !subscription.shop.showCancel || (billingPolicyMinCycles && billingPolicyMinCycles > billingAttempts.length) ? (
        <></>
      ) : (
        <div className="huckleberry-subscription-detail-btn-group">
          <button
            className="huckleberry-subscription-detail-btn cancel"
            disabled={subscription.disabled}
            onClick={() => {
              updateStatusOpen('cancelled');
            }}
          >
            定期購入の解約
          </button>
        </div>
      );
  }

  const address =
    deliveryFormattedAddress && deliveryFormattedAddress.length > 0 ? (
      /*
      <>
        <dl className="huckleberry-subscription-detail-delivery-content">
          <dt className="label">住所：</dt>
          <dd
            className="value"
            dangerouslySetInnerHTML={{
              __html: deliveryFormattedAddress.slice(2).join('<br/>'),
            }}
          />
        </dl>
        <dl className="huckleberry-subscription-detail-delivery-content">
          <dt className="label">氏名：</dt>
          <dd className="value">{deliveryFormattedAddress[0]}</dd>
        </dl>
        <dl className="huckleberry-subscription-detail-delivery-content">
          <dt className="label">電話番号：</dt>
          <dd className="value">{deliveryFormattedAddress[1]}</dd>
        </dl>
      </>
       */
      <p
        dangerouslySetInnerHTML={{
          __html: deliveryFormattedAddress.join('</br>'),
        }}
      />
    ) : (
      <p>なし</p>
    );

  const addressButton =
    subscription.status !== 'CANCELLED' && subscription.shop.showChangeAddress ? (
      <Link
        className="huckleberry-subscription-detail-delivery-btn change"
        to={`/subscriptions/${encodeURIComponent(subscription.subscriptionContractId)}/edit`}
      >
        編集
      </Link>
    ) : (
      <></>
    );

  const mypageRemarks = subscription.shop.mypageRemarks ? (
    <div className="huckleberry-subscription-detail-memo">
      <p>{subscription.shop.mypageRemarks}</p>
    </div>
  ) : (
    <></>
  );

  // TODO: 表示するランクを設定
  const rank = false ? (
    <div className="huckleberry-subscription-detail-rank">
      <span className="item">Cランク</span>
      <span className="item">3%割引</span>
    </div>
  ) : (
    <></>
  );

  let deliveryDaysView = <></>;
  let deliveryChangeModalView = <></>;
  let deliveryChangeCompleteModalView = <></>;
  if (deliveryDate && deliveryDate.current) {
    if (subscription.shop.showDelivery) {
      deliveryDaysView = (
        <article className="huckleberry-subscription-detail-schedule">
          <header>
            <h2 className="huckleberry-subscription-detail-schedule-title">お届け予定日</h2>
          </header>
          <main>
            <p>次回お届け予定日：{deliveryDate.current.format('YYYY年M月D日')}</p>
          </main>
          <footer>
            <button
              className="huckleberry-subscription-detail-schedule-btn change"
              disabled={!subscription.shop.mypageChangeDeliveryDays}
              onClick={() => {
                updateStatusOpen('delivery-days');
              }}
            >
              変更
            </button>
          </footer>
        </article>
      );
    }

    if (deliveryDate!.min && deliveryDate!.max && deliveryDate!.current) {
      deliveryChangeModalView = (
        <div className="micromodal" id="modal-delivery-days" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div role="dialog" aria-modal="true" aria-labelledby="modal-delivery-days-title">
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-delivery-days-title">お届け予定日の変更</h2>
              </header>
              <main id="modal-delivery-days-content">
                <p>
                  次回ご注文のお届け予定日を変更します。
                  <br />
                  ご希望の日を選んで「保存」してください。
                </p>
                <div className="litepicker-wrap">
                  <DatePicker
                    locale="ja"
                    selected={deliveryDate!.current.toDate()}
                    minDate={deliveryDate!.min.toDate()}
                    maxDate={deliveryDate!.max.toDate()}
                    onChange={nextDeliveryDateChange}
                    filterDate={isDisableDays}
                    inline
                  />
                </div>
              </main>
              <footer>
                <button className="btn" onClick={updateDeliveryDays}>
                  保存
                </button>
              </footer>
            </div>
          </div>
        </div>
      );

      deliveryChangeCompleteModalView = (
        <div className="micromodal" id="modal-delivery-days-complete" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div role="dialog" aria-modal="true" aria-labelledby="modal-delivery-days-complete-title">
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-delivery-days-complete-title">お届け予定日の変更</h2>
              </header>
              <main id="modal-delivery-days-complete-content">
                <p>
                  次回ご注文のお届け予定日を変更しました。
                  <br />
                  次回お支払い日： {dayjs(nextBillingDate).format('YYYY年M月D日')}
                  <br />
                  次回お届け予定日： {deliveryDate.change.format('YYYY年M月D日')}
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      <header>
        <h2 className="huckleberry-subscription-detail-title">定期購買 #{subscriptionContractId.split('/')[4]}</h2>
        {mypageRemarks}
        {rank}
      </header>
      <div className="wrap">
        <div className="content">
          <section>
            {subscription.subscriptionLines.map((line, index) => {
              // TODO
              const currency =
                line.lineDiscountedPriceCurrencyCode === 'JPY' ? '円' : line.lineDiscountedPriceCurrencyCode;
              const title = line.variantTitle ? `${line.title} - ${line.variantTitle}` : line.title;

              return (
                <article key={index} style={{ margin: '30px 0' }}>
                  <dl className="huckleberry-subscription-detail-product-detail">
                    <dt className="huckleberry-subscription-detail-product-title">商品</dt>
                    <dd className="huckleberry-subscription-detail-product-name">
                      <a
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          window.parent.postMessage({ action: 'link', url: line.onlineStorePreviewUrl }, '*');
                        }}
                      >
                        {title}
                      </a>
                    </dd>
                  </dl>
                  <dl className="huckleberry-subscription-detail-product-detail">
                    <dt className="label">SKU</dt>
                    <dd className="value">{line.sku}</dd>
                  </dl>
                  <dl className="huckleberry-subscription-detail-product-detail">
                    <dt className="label">価格</dt>
                    <dd className="value">
                      <NumberFormat
                        displayType="text"
                        value={line.currentPriceAmount}
                        thousandSeparator={true}
                        suffix={currency}
                      />
                    </dd>
                  </dl>
                  <dl className="huckleberry-subscription-detail-product-detail">
                    <dt className="label">個数</dt>
                    <dd className="value">
                      <NumberFormat displayType="text" value={line.quantity} thousandSeparator={true} />
                    </dd>
                  </dl>
                  <dl className="huckleberry-subscription-detail-product-detail">
                    <dt className="label">小計</dt>
                    <dd className="value">
                      <NumberFormat
                        displayType="text"
                        value={line.lineDiscountedPriceAmount}
                        thousandSeparator={true}
                        suffix={currency}
                      />
                    </dd>
                  </dl>
                  {/*
                  <div className="huckleberry-subscription-detail-btn-group">
                    <button className="huckleberry-subscription-detail-btn delete">削除</button>
                    <button className="huckleberry-subscription-detail-btn change">商品の変更</button>
                  </div>
                  */}
                </article>
              );
            })}
            <article style={{ margin: '30px 0', borderTop: '1px solid #000' }}>
              <dl className="huckleberry-subscription-detail-product-summary">
                <dt className="label">送料</dt>
                <dd className="value">
                  <NumberFormat
                    displayType="text"
                    value={deliveryPriceAmount}
                    thousandSeparator={true}
                    suffix={deliveryPriceCurrency}
                  />
                </dd>
              </dl>
              <dl className="huckleberry-subscription-detail-product-summary">
                <dt className="label">個数</dt>
                <dd className="value">
                  <NumberFormat value={1} displayType="text" />
                </dd>
              </dl>
              <dl className="huckleberry-subscription-detail-product-summary">
                <dt className="label">合計</dt>
                <dd className="value">
                  <NumberFormat
                    displayType="text"
                    value={deliveryPriceAmount}
                    thousandSeparator={true}
                    suffix={deliveryPriceCurrency}
                  />
                </dd>
              </dl>
              {/*
              <div className="huckleberry-subscription-detail-btn-group">
                <button className="huckleberry-subscription-detail-btn add">追加購入する</button>
              </div>
              */}
            </article>
          </section>

          <section>
            <header style={{ marginTop: '30px' }}>
              <h2 className="huckleberry-subscription-detail-history-title">注文履歴</h2>
            </header>

            {billingAttempts.map((billingAttempt, index) => {
              return (
                <article key={index}>
                  <dl className="huckleberry-subscription-detail-history-detail">
                    <dt className="huckleberry-subscription-detail-history-date-title">日時</dt>
                    <dd className="huckleberry-subscription-detail-history-date">
                      <time>{dayjs(billingAttempt.createdAt).format('YYYY年MM月DD日')}</time>
                    </dd>
                  </dl>
                  <dl className="huckleberry-subscription-detail-history-detail">
                    <dt className="label">注文番号</dt>
                    <dd className="value">
                      <a
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          window.parent.postMessage(
                            {
                              action: 'link',
                              url: `/account/orders/${billingAttempt.orderToken}`,
                            },
                            '*'
                          );
                        }}
                      >
                        {billingAttempt.orderName}
                      </a>
                    </dd>
                  </dl>
                  <dl className="huckleberry-subscription-detail-history-detail">
                    <dt className="label">ステータス</dt>
                    <dd className="value">{billingAttempt.status}</dd>
                  </dl>
                </article>
              );
            })}
          </section>
        </div>
        <div className="content">
          <section>
            {deliveryDaysView}
            <article className="huckleberry-subscription-detail-order">
              <header>
                <h2 className="huckleberry-subscription-detail-order-title">注文内容</h2>
              </header>
              <main>
                <p>
                  契約状況：{statusMessages[status as StatusMessage]}
                  <br />
                  お届け周期： {billingPolicyIntervalCount + billingPolicyIntervals[billingPolicyInterval]}
                  <br />
                  次回支払い予定日：{dayjs(nextBillingDate).format('YYYY年MM月DD日')}
                </p>
              </main>
              <footer>
                {/*
                <button className="huckleberry-subscription-detail-order-btn change">プランの変更</button>
                */}
                <div className="huckleberry-subscription-detail-order-btn-group">
                  {skipButton}
                  {pauseOrActiveButton}
                </div>
              </footer>
            </article>

            <article className="huckleberry-subscription-detail-delivery">
              <header>
                <h2 className="huckleberry-subscription-detail-delivery-title">配送住所</h2>
              </header>
              <main>{address}</main>
              <footer>{addressButton}</footer>
            </article>

            {/*
            <article className="huckleberry-subscription-detail-payment">
              <header>
                <h2 className="huckleberry-subscription-detail-payment-title">お支払い方法</h2>
              </header>
              <main>
                <p>クレジットカード</p>
              </main>
              <footer>
                <button className="huckleberry-subscription-detail-payment-btn change">変更</button>
              </footer>
            </article>
            */}

            {cancelButton}
          </section>

          {deliveryChangeModalView}
          {deliveryChangeCompleteModalView}

          <div className="micromodal" id="modal-skip" aria-hidden="true">
            <div className="micromodal-overlay" tabIndex={-1}>
              <div role="dialog" aria-modal="true" aria-labelledby="modal-skip-title">
                <header>
                  <button aria-label="Close modal" data-micromodal-close></button>
                  <h2 id="modal-skip-title">定期購入のスキップ</h2>
                </header>
                <main id="modal-skip-content">
                  <p>スキップしてもよろしいですか？</p>
                </main>
                <footer>
                  <button className="btn" onClick={skip}>
                    スキップ
                  </button>
                </footer>
              </div>
            </div>
          </div>
          <div className="micromodal" id="modal-skip-complete" aria-hidden="true">
            <div className="micromodal-overlay" tabIndex={-1}>
              <div role="dialog" aria-modal="true" aria-labelledby="modal-skip-complete-title">
                <header>
                  <button aria-label="Close modal" data-micromodal-close></button>
                  <h2 id="modal-skip-complete-title">定期購入のスキップ</h2>
                </header>
                <main id="modal-skip-complete-content">
                  <p>
                    スキップを受け付けました。
                    <br />
                  </p>
                </main>
                <footer>
                  <button className="btn" data-micromodal-close>
                    ＯＫ
                  </button>
                </footer>
              </div>
            </div>
          </div>

          <div className="micromodal" id="modal-paused" aria-hidden="true">
            <div className="micromodal-overlay" tabIndex={-1}>
              <div role="dialog" aria-modal="true" aria-labelledby="modal-paused-title">
                <header>
                  <button aria-label="Close modal" data-micromodal-close></button>
                  <h2 id="modal-paused-title">定期購買の一時停止</h2>
                </header>
                <main id="modal-paused-content">
                  <p>定期購買を一時停止しますか？</p>
                </main>
                <footer>
                  <button
                    className="btn"
                    onClick={() => {
                      updateStatus('paused');
                    }}
                  >
                    一時停止
                  </button>
                </footer>
              </div>
            </div>
          </div>
          <div className="micromodal" id="modal-paused-complete" aria-hidden="true">
            <div className="micromodal-overlay" tabIndex={-1}>
              <div role="dialog" aria-modal="true" aria-labelledby="modal-paused-complete-title">
                <header>
                  <button aria-label="Close modal" data-micromodal-close></button>
                  <h2 id="modal-paused-complete-title">定期購買の一時停止</h2>
                </header>
                <main id="modal-paused-complete-content">
                  <p>
                    定期購買を一時停止しました。
                    <br />
                    再開は注文内容から行えます。
                  </p>
                </main>
                <footer>
                  <button className="btn" data-micromodal-close>
                    ＯＫ
                  </button>
                </footer>
              </div>
            </div>
          </div>

          <div className="micromodal" id="modal-active" aria-hidden="true">
            <div className="micromodal-overlay" tabIndex={-1}>
              <div role="dialog" aria-modal="true" aria-labelledby="modal-active-title">
                <header>
                  <button aria-label="Close modal" data-micromodal-close></button>
                  <h2 id="modal-actove-title">定期購買の再開</h2>
                </header>
                <main id="modal-active-content">
                  <p>
                    定期購買を再開しますか？
                    <br />
                    次回のお届け日は
                    {deliveryDate && deliveryDate!.current ? deliveryDate!.current.format('YYYY年M月D日') : ''}
                    です。
                    <br />
                    ※再開後、「お届け予定日」からお届け日の変更ができます。
                  </p>
                </main>
                <footer>
                  <button
                    className="btn"
                    onClick={() => {
                      updateStatus('active');
                    }}
                  >
                    再開
                  </button>
                </footer>
              </div>
            </div>
          </div>
          <div className="micromodal" id="modal-active-complete" aria-hidden="true">
            <div className="micromodal-overlay" tabIndex={-1}>
              <div role="dialog" aria-modal="true" aria-labelledby="modal-active-complete-title">
                <header>
                  <button aria-label="Close modal" data-micromodal-close></button>
                  <h2 id="modal-active-complete-title">定期購買の再開</h2>
                </header>
                <main id="modal-active-complete-content">
                  <p>
                    定期購買を再開しました。
                    <br />
                    次回お支払い日：
                    {dayjs(nextBillingDate).format('YYYY年M月D日')}
                    <br />
                    次回お届け予定日：
                    {deliveryDate && deliveryDate!.current ? deliveryDate!.current.format('YYYY年M月D日') : ''}
                  </p>
                </main>
                <footer>
                  <button className="btn" data-micromodal-close>
                    ＯＫ
                  </button>
                </footer>
              </div>
            </div>
          </div>

          <div className="micromodal" id="modal-cancelled" aria-hidden="true">
            <div className="micromodal-overlay" tabIndex={-1}>
              <div role="dialog" aria-modal="true" aria-labelledby="modal-cancelled-title">
                <header>
                  <button aria-label="Close modal" data-micromodal-close></button>
                  <h2 id="modal-cancelled-title">定期購買の解約</h2>
                </header>
                <main id="modal-cancelled-content">
                  <p>
                    定期購買を解約します。
                    <br />
                    ※既に決済が完了したご注文はキャンセルされません。
                  </p>
                </main>
                <footer>
                  <button
                    className="btn"
                    onClick={() => {
                      updateStatus('cancelled');
                    }}
                  >
                    解約
                  </button>
                  <button className="btn clear" data-micromodal-close>
                    戻る
                  </button>
                </footer>
              </div>
            </div>
          </div>
          <div className="micromodal" id="modal-cancelled-complete" aria-hidden="true">
            <div className="micromodal-overlay" tabIndex={-1}>
              <div role="dialog" aria-modal="true" aria-labelledby="modal-cancelled-complete-title">
                <header>
                  <button aria-label="Close modal" data-micromodal-close></button>
                  <h2 id="modal-cancelled-complete-title">定期購買の解約</h2>
                </header>
                <main id="modal-cancelled-complete-content">
                  <p>
                    定期購買を解約しました。
                    <br />
                    再度ご注文される場合は、もう一度定期購買をご注文ください。
                    <br />
                  </p>
                </main>
                <footer>
                  <button className="btn" data-micromodal-close>
                    ＯＫ
                  </button>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
